import AWS from "aws-sdk";
import { config } from "./config/amplify";
import { Storage, Auth } from "aws-amplify";
import { tuple } from "antd/es/_util/type";
import { processConfig } from "./config/env";
import {
  CognitoUserPool,
  CognitoUser,
  CognitoRefreshToken,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoUserSession,
} from "amazon-cognito-identity-js"

const region = config.Auth.region;
const userPoolId = config.Auth.userPoolId;
const identityPoolId = config.Auth.identityPoolId;
const clientId = config.Auth.userPoolWebClientId;
const S3_BUCKET_ARCHFORM_PATIENT_DATA_V1 = processConfig.env.S3_BUCKET_ARCHFORM_PATIENT_DATA_V1

// Cognito ClientMetadata — must be strings (EnableCustomCheck included)
export const AUTH_CLIENT_METADATA = {
  ClientType: "ARCHFORM_WEB_VIEWER",
  ClientVersion: "v2",
  EnableCustomCheck: "true",
};

AWS.config.region = processConfig.env.AWS_REGION;

const ModeEnum = Object.freeze({
  Advanced: "Advanced",
  ReadOnly: "Read-Only",
});

/**
 * Get Encrypt Params from Path
 * @returns {Params}
 */
export const getEncryptDataFromPath = () => {
  try {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const data = params.get("data");
    const name = params.get("name");

    let queryData = window.atob(data);
    let parsedObject = JSON.parse(queryData);

    // Todo: show patient name from link
    let displayLabel = name
    const { displayLabel: label } = parsedObject

    if(label) {
      displayLabel = label
    }

    /* Params:
      {
        displayLabel: string,
        userId: string,
        path: string,
        mode: WebViewerModeEnum.ReadOnly,
        ==============>
        refreshToken: string,

        userId: userId,
        path: path,
        mode: WebViewerModeEnum.Advanced,
        ==============>
        "username": string,
        "password": string,

        "userId": string,
        "path": string,
        "mode": WebViewerModeEnum.Advanced,
      }
    */
    // console.log('>>>>>queryData', parsedObject)
    return {
      ...parsedObject,
      displayLabel,
    };
  } catch (e) {
    return { mode: "Read-Only" };
  }
};

/**
 * Get Open Mode by Path Params
 */
export const getMode = () => {
  const { mode } = getEncryptDataFromPath();
  return {
    readOnlyMode: mode === ModeEnum.ReadOnly,
    advancedMode: mode === ModeEnum.Advanced,
  };
};

export const getIsShareableLinkMode = () => {
  const { userId, path } = getEncryptDataFromPath();

  if (!userId && !path) {
    return true;
  }

  return false;
};

export const getAWSS3ServiceClient = async (token) => {
  try {
    // Cognito User Pools
    const providerName = `cognito-idp.${config.Auth.region}.amazonaws.com/${config.Auth.userPoolId}`;

    const cognitoidentity = new AWS.CognitoIdentity();

    // Cognito Identity Pool Credentials Params
    const params = {
      IdentityPoolId: config.Auth.identityPoolId,
      Logins: {
        [providerName]: token,
      },
    };

    // get Identity Id
    const identityResult = await cognitoidentity.getId(params).promise();

    const cognitoIdentityCredentials = new AWS.CognitoIdentityCredentials({
      IdentityId: identityResult.IdentityId,
      Logins: params.Logins,
    });

    // Update Credentials
    await cognitoIdentityCredentials.getPromise();

    AWS.config.credentials = cognitoIdentityCredentials;
  } catch (error) {
    console.error("Failed to retrieve AWS credentials:", error);
    throw error;
  }
};

export const handleRefreshToken = async (refreshToken, clientId) => {
  const region = config.Auth.region;

  try {
    // GetTokensFromRefreshToken (not InitiateAuth/REFRESH_TOKEN_AUTH) is required
    // for ClientMetadata to reach the Pre Token Generation Lambda.
    const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target":
          "AWSCognitoIdentityProviderService.GetTokensFromRefreshToken",
      },
      body: JSON.stringify({
        ClientId: clientId,
        RefreshToken: refreshToken,
        ClientMetadata: AUTH_CLIENT_METADATA,
      }),
    });

    const data = await response.json();
    if (!response.ok) {
      const error = new Error(
        data.message || data.__type || "GetTokensFromRefreshToken failed"
      );
      error.name = data.__type || "Error";
      error.code = data.__type;
      throw error;
    }

    const { IdToken, AccessToken, RefreshToken } = data.AuthenticationResult;
    return {
      newIdToken: IdToken,
      newAccessToken: AccessToken,
      newRefreshToken: RefreshToken || refreshToken,
    };
  } catch (error) {
    console.error("Error refreshing tokens:", error);
    throw error;
  }
};

export const getStoragePhoto = async ({ user_id, patient_id, file_name }) => {
  const key = `${patient_id}/composite/${file_name}.jpg`;

  const options = {
    level: "protected",
    identityId: user_id,
    bucket: S3_BUCKET_ARCHFORM_PATIENT_DATA_V1,
  };
  const res = await Storage.get(key, options);
  return res;
};

export const getStorageXray = async ({ user_id, patient_id, file_name }) => {
  const XRAY_ADEPTER = {
    panoramic: "xray_pano",
    cephalometric: "xray_fmx",
  };
  const key = `${patient_id}/xray/${XRAY_ADEPTER[file_name]}.jpg`;

  const options = {
    level: "protected",
    identityId: user_id,
    bucket: S3_BUCKET_ARCHFORM_PATIENT_DATA_V1,
  };
  const res = await Storage.get(key, options);

  return res;
};

const checkImagePromise = (link) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = link;
    img.onload = function () {
      console.log("image load success:");
      resolve(link);
    };
    img.onerror = function () {
      console.log("image load failed:");
      reject(null);
    };
  });
};

export const checkImageLink = async (link) => {
  try {
    const res = await checkImagePromise(link);
    return res;
  } catch {
    return null;
  }
};

export const getToothNumberFromFilePath = (str) => {
  const regex = /TOOTH_(\d+)/;
  const match = str.match(regex);   
  if (match) {
    return parseInt(match[1], 10);
  } 

  throw new Error("GetToothNumberFromFilePath fail!")
}

export const getStageNumberFromFilePath = (str) => {
  const regex = /STAGE_(\d+)/;
  const match = str.match(regex);   
  if (match) {
    return parseInt(match[1], 10);
  } 

  throw new Error("GetStageNumberFromFilePath fail!")
}

function base64UrlToUtf8Json(b64url) {
  let b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4;
  if (pad) b64 += "=".repeat(4 - pad);
  return JSON.parse(atob(b64));
}

export const loginByRefreshToken = async (refreshToken) => {
  const { userPoolWebClientId, userPoolId } = config.Auth;

  const { newIdToken, newAccessToken, newRefreshToken } =
    await handleRefreshToken(refreshToken, userPoolWebClientId);

  const idTokenPayload = base64UrlToUtf8Json(newIdToken.split(".")[1]);
  const username =
    idTokenPayload["cognito:username"] || idTokenPayload.sub;

  const pool = new CognitoUserPool({
    UserPoolId: userPoolId,
    ClientId: userPoolWebClientId,
    Storage: config.Storage || {},
  });

  const user = new CognitoUser({
    Username: username,
    Pool: pool,
  });

  // Use tokens from GetTokensFromRefreshToken directly — do not call
  // refreshSession (InitiateAuth), which drops ClientMetadata in Pre Token Gen.
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: newIdToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: newAccessToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: newRefreshToken }),
  });

  user.setSignInUserSession(session);
};